// Everything here is made possible by
// https://draftjs.org/
// https://materialdesignicons.com/
import React, { useRef, useState } from 'react';
import { Editor, EditorState, RichUtils, convertFromHTML, ContentState } from 'draft-js';
import 'draft-js/dist/Draft.css';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';

export function ContentEditor({ title = '', description = '', value = '', output, error = false, disabled = false }) {
    const el = useRef();
    const blocksFromHTML = convertFromHTML(value);
    const content = ContentState.createFromBlockArray(blocksFromHTML);
    const [editorState, setEditorState] = useState(() => EditorState.createWithContent(content));
    const focus = () => el.current.focus();
    const onChange = editorState => {
        setEditorState(editorState);
        let _text = el.current.editor.innerText;
        let _test = _text.replace(/^\s+|\s+$/g, '');
        output({
            html: _test !== '' ? el.current.editor.innerHTML : '',
            text: _test !== '' ? el.current.editor.innerText : '',
        });
    };

    const handleKeyCommand = command => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            onChange(newState);
            return true;
        }
        return false;
    };
    const toggleBlockType = blockType => {
        onChange(RichUtils.toggleBlockType(editorState, blockType));
    };
    const toggleInlineStyle = inlineStyle => {
        onChange(
            RichUtils.toggleInlineStyle(editorState, inlineStyle)
        );
    };
    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let className = 'RichEditor-editor';
    var contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
        if (contentState.getBlockMap().first().getType() !== 'unstyled') {
            className += ' RichEditor-hidePlaceholder';
        }
    }
    return (
        <>
            {(title !== '') && <p style={{ marginBottom: '4px', color: error ? '#f018a6' : 'var(--mn-text-50)', fontSize: '1rem' }}>{title}</p>}
            <div className={"d-block RichEditor-root" + (error ? ' is-error' : '')}>
                {disabled && <div className="RichEditor-disabled" />}
                <BlockStyleControls
                    editorState={editorState}
                    onToggle={toggleBlockType}
                />
                <InlineStyleControls
                    editorState={editorState}
                    onToggle={toggleInlineStyle}
                />
                <div className={className} onClick={focus}>
                    <Editor
                        blockStyleFn={getBlockStyle}
                        customStyleMap={styleMap}
                        editorState={editorState}
                        handleKeyCommand={handleKeyCommand}
                        onChange={onChange}
                        placeholder={(title !== '') ? `${title} here...` : `Anything here...`}
                        ref={el}
                        spellCheck={true}
                    />
                </div>
            </div>
            {(description !== '') && <p style={{ marginTop: '10px', marginBottom: '12px', color: error ? '#f018a6' : 'var(--mn-text-50)', fontSize: '0.75rem', lineHeight: '1' }}>{description}</p>}
        </>
    );
}

const styleMap = {
    CODE: {
        backgroundColor: 'var(--mn-background-alt)',
        fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
        fontSize: 16,
        padding: 2,
        color: 'var(--mn-text-70)',
    },
    HIGHLIGHT: {
        backgroundColor: '#faed27',
    }
};
const getBlockStyle = (block) => {
    switch (block.getType()) {
        case 'blockquote':
            return 'RichEditor-blockquote';
        default:
            return null;
    }
}

class FormatButton extends React.Component {
    constructor() {
        super();
        this.onToggle = e => {
            e.preventDefault();
            this.props.onToggle(this.props.style);
        };

    }
    render() {
        return (
            <Button
                onMouseDown={this.onToggle}
                title={this.props.label}
                color={this.props.active ? 'secondary' : 'default'} size="small"
            >
                {(this.props.icon === '') && <span>{this.props.label}</span>}
                {(this.props.icon !== '') && <Icon>{this.props.icon}</Icon>}
            </Button>
        );
    }
}

const BLOCK_TYPES = [
    { label: 'H1', icon: '', style: 'header-one' },
    { label: 'H2', icon: '', style: 'header-two' },
    { label: 'H3', icon: '', style: 'header-three' },
    { label: 'H4', icon: '', style: 'header-four' },
    { label: 'H5', icon: '', style: 'header-five' },
    { label: 'H6', icon: '', style: 'header-six' },
    { label: 'Blockquote', icon: '', style: 'blockquote' },
    { label: 'UL', icon: 'format_list_bulleted', style: 'unordered-list-item' },
    { label: 'OL', icon: 'format_list_numbered', style: 'ordered-list-item' },
    { label: 'Code Block', icon: '', style: 'code-block' },
];
const BlockStyleControls = props => {
    const { editorState } = props;
    const selection = editorState.getSelection();
    const blockType = editorState
        .getCurrentContent()
        .getBlockForKey(selection.getStartKey())
        .getType();
    return (
        <div className="RichEditor-controls">
            {BLOCK_TYPES.map(type =>
                <FormatButton
                    key={type.label}
                    active={type.style === blockType}
                    label={type.label}
                    icon={type.icon}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

var INLINE_STYLES = [
    { label: 'Bold', icon: 'format_bold', style: 'BOLD' },
    { label: 'Italic', icon: 'format_italic', style: 'ITALIC' },
    { label: 'Underline', icon: 'format_underline', style: 'UNDERLINE' },
    { label: 'Highlight', icon: '', style: 'HIGHLIGHT' },
    { label: 'Code', icon: 'code_tags', style: 'CODE' },
];
const InlineStyleControls = props => {
    var currentStyle = props.editorState.getCurrentInlineStyle();
    return (
        <div className="RichEditor-controls">
            {INLINE_STYLES.map(type =>
                <FormatButton
                    key={type.label}
                    active={currentStyle.has(type.style)}
                    label={type.label}
                    icon={type.icon}
                    onToggle={props.onToggle}
                    style={type.style}
                />
            )}
        </div>
    );
};

